// Safe Dynamic Image Loader using Vite glob imports.
// This ensures that any missing/deleted files do NOT break compilation,
// and gracefully falls back to appropriate placeholders.

const images = import.meta.glob([
  '/src/assets/images/*',
  './assets/images/*',
  '../assets/images/*'
], { eager: true, import: 'default' }) as Record<string, string>;

/**
 * Safely resolves an image filename or path, mapping to the compiled asset.
 * If the asset doesn't exist anymore, it falls back gracefully without breaking Vite compilation.
 */
export function getSafeImage(input: string | undefined): string {
  if (!input) {
    return 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?auto=format&fit=crop&q=80&w=400&h=600';
  }
  
  // If input is already a fully resolved URL (like https://picsum.photos or unsplash), return it directly
  if (input.startsWith('http://') || input.startsWith('https://') || input.startsWith('data:')) {
    return input;
  }

  // Extract the base filename (e.g., "bloombuddy_mockup_1779659858114.png")
  // Clean potential path markers
  const cleanInput = input
    .replace(/^(\.\/|\.\.\/|\/)?(src\/)?assets\/images\//, '')
    .trim()
    .toLowerCase();

  // Try to find a match in the keys of our globbed images
  const keys = Object.keys(images);
  
  // 1. Exact base filename match
  let matchedKey = keys.find(k => k.toLowerCase().endsWith(cleanInput));
  
  // 2. Fuzzy match if exact matches fail (e.g. ignoring extensions or spaces)
  if (!matchedKey) {
    const searchPart = cleanInput.split('.')[0];
    if (searchPart && searchPart.length > 2) {
      matchedKey = keys.find(k => k.toLowerCase().includes(searchPart));
    }
  }

  if (matchedKey && images[matchedKey]) {
    return images[matchedKey];
  }

  // Custom fallback intercept for The Traitor's Vow if the local file is not found/available
  const lowerInput = input.toLowerCase();
  if (lowerInput.includes('vow') || lowerInput.includes('traitor')) {
    return 'https://images.unsplash.com/photo-1514539079130-25950c84af65?q=80&w=600&h=850&auto=format&fit=crop';
  }

  // Custom fallback intercept for Sam and the Shadow Gremlins
  if (lowerInput.includes('gremlins') || lowerInput.includes('shadow_gremlins')) {
    const localKey = keys.find(k => k.toLowerCase().includes('shadow_gremlins_cover.png'));
    if (localKey && images[localKey]) {
      return images[localKey];
    }
    return '/src/assets/images/shadow_gremlins_cover.png';
  }

  // Custom fallback intercept for The Snaggle-Toothed Beast of Boggle-Down Fen
  if (lowerInput.includes('snaggle') || lowerInput.includes('beast') || lowerInput.includes('boggle')) {
    return 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?q=80&w=600&h=850&auto=format&fit=crop';
  }

  // Custom high-contrast phone mockup intercept for Bowls Captain Communication to prevent right-border-merging issues
  if (lowerInput.includes('bowls_captain_comms') || lowerInput.includes('captain_comms')) {
    return 'https://images.unsplash.com/photo-1551650975-87deedd944c3?q=80&w=800&auto=format&fit=crop';
  }

  // Fallback for Bowls Analyser app
  if (lowerInput.includes('bowls_analyser') || lowerInput.includes('analyser')) {
    return 'https://images.unsplash.com/photo-1508962914676-134849a727f0?q=80&w=800&auto=format&fit=crop';
  }

  // Fallback for CruxSum puzzle app
  if (lowerInput.includes('kakuro')) {
    return 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=800&auto=format&fit=crop';
  }

  // Fallback for Numerax daily math app
  if (lowerInput.includes('numera')) {
    return 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?q=80&w=800&auto=format&fit=crop';
  }

  // Fallback for Quigley Raccoon mobile app
  if (lowerInput.includes('quigley_app') || lowerInput.includes('quigley-app')) {
    const localKey = keys.find(k => k.toLowerCase().includes('quigley_backpack'));
    if (localKey && images[localKey]) {
      return images[localKey];
    }
    return 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?q=80&w=800&auto=format&fit=crop';
  }

  // Graceful Fallback if file was deleted/missing
  if (
    cleanInput.includes('cover') || 
    cleanInput.includes('front') || 
    cleanInput.includes('raccoon') || 
    cleanInput.includes('stinky') || 
    cleanInput.includes('rewind') || 
    cleanInput.includes('cinder') || 
    cleanInput.includes('variance') || 
    cleanInput.includes('ringtail') || 
    cleanInput.includes('sleuths') || 
    cleanInput.includes('lies') || 
    cleanInput.includes('muddle') || 
    cleanInput.includes('pip') ||
    cleanInput.includes('gremlins')
  ) {
    // Elegant book cover placeholder with a nice cover icon/background
    return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?q=80&w=600&auto=format&fit=crop';
  }

  // App mockup placeholder
  return 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=600&auto=format&fit=crop';
}
